import React, { useEffect, useState, useMemo } from 'react';
import { Box, Typography, Paper, Skeleton, Alert, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Chip, Grid, Card, CardContent, Avatar } from '@mui/material';
import MainLayout from '../layouts/MainLayout';
import { getRantingMonitoringData } from '../services/mwcService';

// Icons
import ReceiptIcon from '@mui/icons-material/Receipt';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import VisibilityIcon from '@mui/icons-material/Visibility';

// --- Reusable Widget ---
const StatWidget = ({ title, value, icon, color, loading }) => (
  <Card sx={{ height: '100%', position: 'relative', overflow: 'hidden', boxShadow: 3, borderRadius: 2 }}>
    <Box sx={{ position: 'absolute', top: -15, right: -15, opacity: 0.1, transform: 'rotate(-10deg) scale(2.5)', color: color }}>
        {icon}
    </Box>
    <CardContent sx={{ display: 'flex', alignItems: 'center', p: 3 }}>
        <Avatar sx={{ bgcolor: `${color}15`, color: color, width: 64, height: 64, mr: 2.5 }}>
            {icon}
        </Avatar>
        <Box>
            <Typography variant="body2" color="text.secondary" fontWeight="medium" sx={{ mb: 0.5 }}>{title}</Typography>
            <Typography variant="h5" fontWeight="bold" sx={{ color: 'text.primary' }}>
                {loading ? <Skeleton width={100} /> : value}
            </Typography>
        </Box>
    </CardContent>
  </Card>
);

const RantingMonitoringPage = () => {
  const [monitoringData, setMonitoringData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const data = await getRantingMonitoringData();
        setMonitoringData(data || []);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const stats = useMemo(() => {
    return monitoringData.reduce((acc, tx) => {
        acc.count++;
        acc.pemasukan += (tx.totalPendapatan || 0);
        acc.pengeluaran += (tx.totalBeban || 0);
        return acc;
    }, { count: 0, pemasukan: 0, pengeluaran: 0 });
  }, [monitoringData]);

  const getKategoriName = (t) => t.kategori ? t.kategori.nama : '-';

  if (error) {
    return (
      <MainLayout>
        <Box sx={{ p: 3 }}><Alert severity="error">Gagal memuat data monitoring: {error}</Alert></Box>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <Box sx={{ p: 3, bgcolor: '#f5f7fa', minHeight: '100vh' }}>
        
        {/* HEADER */}
        <Paper elevation={0} sx={{ p: 3, mb: 4, borderRadius: 3, border: '1px solid #e0e0e0', display: 'flex', alignItems: 'center' }}>
            <Avatar sx={{ bgcolor: 'warning.light', color: 'warning.main', mr: 2 }}>
                <VisibilityIcon />
            </Avatar>
            <Box>
                <Typography variant="h5" fontWeight="bold" color="primary.main">Monitoring Ranting</Typography>
                <Typography variant="body2" color="text.secondary">Pemantauan real-time transaksi yang masuk dari seluruh Unit Ranting.</Typography>
            </Box>
        </Paper>

        {/* HERO STATS */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid item xs={12} md={4}>
                <StatWidget title="Total Transaksi Masuk" value={stats.count} icon={<ReceiptIcon fontSize="large" />} color="#ed6c02" loading={loading} />
            </Grid>
            <Grid item xs={12} md={4}>
                <StatWidget title="Total Pemasukan Ranting" value={`Rp${stats.pemasukan.toLocaleString('id-ID')}`} icon={<TrendingUpIcon fontSize="large" />} color="#2e7d32" loading={loading} />
            </Grid>
            <Grid item xs={12} md={4}>
                <StatWidget title="Total Pengeluaran Ranting" value={`Rp${stats.pengeluaran.toLocaleString('id-ID')}`} icon={<TrendingDownIcon fontSize="large" />} color="#d32f2f" loading={loading} />
            </Grid>
        </Grid>

        {/* TABLE SECTION */}
        <Card sx={{ boxShadow: 3, borderRadius: 2 }}>
            <CardContent>
                <Typography variant="h6" fontWeight="bold" gutterBottom sx={{ mb: 3 }}>Detail Riwayat Transaksi</Typography>
                {loading ? (
                  <Skeleton variant="rectangular" width="100%" height={400} />
                ) : (
                  <TableContainer>
                    <Table sx={{ minWidth: 650 }} aria-label="ranting monitoring table">
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 'bold', bgcolor: '#f5f5f5' }}>No</TableCell>
                          <TableCell sx={{ fontWeight: 'bold', bgcolor: '#f5f5f5' }}>Tanggal</TableCell>
                          <TableCell sx={{ fontWeight: 'bold', bgcolor: '#f5f5f5' }}>Unit Ranting</TableCell>
                          <TableCell sx={{ fontWeight: 'bold', bgcolor: '#f5f5f5' }}>Kategori</TableCell>
                          <TableCell sx={{ fontWeight: 'bold', bgcolor: '#f5f5f5' }}>Deskripsi</TableCell>
                          <TableCell sx={{ fontWeight: 'bold', bgcolor: '#f5f5f5' }}>Dibuat Oleh</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 'bold', bgcolor: '#f5f5f5' }}>Pemasukan</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 'bold', bgcolor: '#f5f5f5' }}>Pengeluaran</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {monitoringData.length === 0 ? (
                          <TableRow>
                            <TableCell colSpan={8} align="center" sx={{ py: 3 }}>
                              <Typography color="text.secondary">Belum ada data transaksi dari Ranting.</Typography>
                            </TableCell>
                          </TableRow>
                        ) : (
                          monitoringData.map((transaction, index) => {
                            const { totalPendapatan, totalBeban } = transaction;
                            const isPemasukan = totalPendapatan > 0;

                            return (
                              <TableRow key={transaction.id} hover>
                                <TableCell component="th" scope="row">{index + 1}</TableCell>
                                <TableCell>{new Date(transaction.tanggal).toLocaleDateString('id-ID')}</TableCell>
                                <TableCell sx={{ fontWeight: 'medium' }}>{transaction.ranting?.name || 'N/A'}</TableCell>
                                <TableCell>
                                    <Chip 
                                        label={getKategoriName(transaction)} 
                                        size="small" 
                                        color={isPemasukan ? "success" : "warning"} 
                                        variant="outlined" 
                                    />
                                </TableCell>
                                <TableCell>{transaction.deskripsi}</TableCell>
                                <TableCell sx={{ color: 'text.secondary' }}>{transaction.user?.username || '-'}</TableCell>
                                <TableCell align="right" sx={{ color: totalPendapatan > 0 ? 'success.main' : 'inherit', fontWeight: totalPendapatan > 0 ? 'bold' : 'normal' }}>
                                  {totalPendapatan > 0 ? `Rp${totalPendapatan.toLocaleString('id-ID')}` : '-'}
                                </TableCell>
                                <TableCell align="right" sx={{ color: totalBeban > 0 ? 'error.main' : 'inherit', fontWeight: totalBeban > 0 ? 'bold' : 'normal' }}>
                                  {totalBeban > 0 ? `Rp${totalBeban.toLocaleString('id-ID')}` : '-'}
                                </TableCell>
                              </TableRow>
                            );
                          })
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
            </CardContent>
        </Card>
      </Box>
    </MainLayout>
  );
};

export default RantingMonitoringPage;